import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  CheckCircle,
  Database,
  RefreshCw,
  AlertCircle,
  BarChart3,
  MapPin,
  FileText
} from 'lucide-react';

export const DataQualityTab: React.FC = () => {
  // Mock data for demonstration
  const healthScore = 87;

  const freshnessMetrics = [
    {
      id: 'recalls',
      name: 'Recall Data',
      lastUpdated: '2 hours ago',
      status: 'healthy',
      icon: Database
    },
    {
      id: 'dealers',
      name: 'Dealer Info',
      lastUpdated: '1 day ago',
      status: 'healthy',
      icon: MapPin
    },
    {
      id: 'campaigns',
      name: 'Campaign Data',
      lastUpdated: '3 days ago',
      status: 'warning',
      icon: FileText
    }
  ];

  const completenessMetrics = [
    {
      name: 'Dealer Geocoding',
      value: 98,
      target: 100,
      description: 'Dealers with valid lat/long coordinates'
    },
    {
      name: 'Recall Remedy Instructions',
      value: 85,
      target: 95,
      description: 'Recalls with detailed remedy steps'
    },
    {
      name: 'Customer Contact Info',
      value: 92,
      target: 90,
      description: 'Customers with valid email or phone'
    },
    {
      name: 'VIN Decoding',
      value: 99.5,
      target: 100,
      description: 'Successfully decoded VINs'
    }
  ];

  const anomalies = [
    {
      id: 1,
      type: 'Duplicate Dealer',
      description: 'Potential duplicate: "Metro Ford" and "Metro Ford Inc" at same address',
      severity: 'medium',
      detectedAt: '2023-11-25T14:30:00'
    },
    {
      id: 2,
      type: 'Invalid VIN',
      description: 'Batch #402 contains 3 VINs with invalid checksums',
      severity: 'high',
      detectedAt: '2023-11-26T09:15:00'
    },
    {
      id: 3,
      type: 'Conflicting Status',
      description: 'Recall #2023-001 marked as "Closed" but has pending appointments',
      severity: 'low',
      detectedAt: '2023-11-24T11:20:00'
    }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800';
      case 'low':
        return 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className='space-y-6'>
      {/* Top Section: Health Score & Freshness */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        {/* Overall Health Score */}
        <Card className='md:col-span-1'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <ActivityGauge className='h-5 w-5 text-primary' />
              Overall Data Health
            </CardTitle>
            <CardDescription>System-wide quality score</CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col items-center justify-center py-6'>
            <div className='relative flex items-center justify-center h-40 w-40'>
              {/* Simple SVG Circular Progress for demo */}
              <svg className='h-full w-full transform -rotate-90' viewBox='0 0 100 100'>
                <circle
                  className='text-muted/20'
                  strokeWidth='10'
                  stroke='currentColor'
                  fill='transparent'
                  r='40'
                  cx='50'
                  cy='50'
                />
                <circle
                  className={`${
                    healthScore > 90
                      ? 'text-green-500'
                      : healthScore > 70
                        ? 'text-yellow-500'
                        : 'text-red-500'
                  }`}
                  strokeWidth='10'
                  strokeDasharray={`${healthScore * 2.51} 251.2`}
                  strokeLinecap='round'
                  stroke='currentColor'
                  fill='transparent'
                  r='40'
                  cx='50'
                  cy='50'
                />
              </svg>
              <div className='absolute flex flex-col items-center'>
                <span className='text-4xl font-bold'>{healthScore}</span>
                <span className='text-xs text-muted-foreground'>/ 100</span>
              </div>
            </div>
            <div className='mt-4 text-center text-sm text-muted-foreground'>
              Based on completeness, accuracy, and freshness metrics.
            </div>
          </CardContent>
        </Card>

        {/* Freshness Metrics */}
        <Card className='md:col-span-2'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <RefreshCw className='h-5 w-5 text-primary' />
              Data Freshness
            </CardTitle>
            <CardDescription>Time since last successful synchronization</CardDescription>
          </CardHeader>
          <CardContent className='grid gap-4'>
            {freshnessMetrics.map(metric => (
              <div
                key={metric.id}
                className='flex items-center justify-between p-3 border rounded-lg'
              >
                <div className='flex items-center gap-3'>
                  <div className='p-2 bg-muted rounded-full'>
                    <metric.icon className='h-5 w-5 text-muted-foreground' />
                  </div>
                  <div>
                    <p className='font-medium'>{metric.name}</p>
                    <p className='text-xs text-muted-foreground'>Updated {metric.lastUpdated}</p>
                  </div>
                </div>
                <Badge
                  variant={metric.status === 'healthy' ? 'default' : 'secondary'}
                  className={
                    metric.status === 'healthy'
                      ? 'bg-green-500 hover:bg-green-600'
                      : 'bg-yellow-500 hover:bg-yellow-600'
                  }
                >
                  {metric.status === 'healthy' ? 'Healthy' : 'Warning'}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Middle Section: Completeness & Anomalies */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Completeness Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <BarChart3 className='h-5 w-5 text-primary' />
              Data Completeness
            </CardTitle>
            <CardDescription>Completion rates for critical data fields</CardDescription>
          </CardHeader>
          <CardContent className='space-y-6'>
            {completenessMetrics.map((metric, index) => (
              <div key={index} className='space-y-2'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='font-medium'>{metric.name}</span>
                  <span className='text-muted-foreground'>{metric.value}%</span>
                </div>
                <Progress value={metric.value} className='h-2' />
                <p className='text-xs text-muted-foreground'>{metric.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Anomalies List */}
        <Card>
          <CardHeader className='flex flex-row items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                <AlertTriangle className='h-5 w-5 text-primary' />
                Detected Anomalies
              </CardTitle>
              <CardDescription>Potential data quality issues requiring attention</CardDescription>
            </div>
            <Button variant='outline' size='sm'>
              View All
            </Button>
          </CardHeader>
          <CardContent className='space-y-4'>
            {anomalies.map(anomaly => (
              <div
                key={anomaly.id}
                className={`p-3 border rounded-lg ${getSeverityColor(anomaly.severity)} bg-opacity-10`}
              >
                <div className='flex items-start justify-between'>
                  <div className='flex items-start gap-2'>
                    <AlertCircle className='h-5 w-5 mt-0.5 shrink-0' />
                    <div>
                      <p className='font-medium text-sm'>{anomaly.type}</p>
                      <p className='text-xs mt-1 opacity-90'>{anomaly.description}</p>
                    </div>
                  </div>
                  <span className='text-xs font-medium uppercase px-2 py-0.5 rounded bg-white/50 dark:bg-black/20'>
                    {anomaly.severity}
                  </span>
                </div>
              </div>
            ))}
            {anomalies.length === 0 && (
              <div className='text-center py-8 text-muted-foreground'>
                <CheckCircle className='h-12 w-12 mx-auto mb-2 text-green-500' />
                <p>No anomalies detected</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Helper component for the gauge icon
const ActivityGauge = ({ className }: { className?: string }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={className}
  >
    <path d='M3.5 12a8.5 8.5 0 1 1 17 0' />
    <path d='M12 2v2' />
    <path d='M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4' />
    <path d='M12 12 2.5 12' />
  </svg>
);
